import {
  FeatureName,
  PermissionValues,
  useCurrentAccount,
  useDeleteMutation,
  useIsFeatureEnabled,
  WorkspaceAsset,
  WorkspaceType,
} from '@gripp/shared-logic';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Platform, Pressable, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { Button } from '../crop-circle/Button';
import { DeleteAsset as DeleteAssetOld } from './deleteAsset-old';

type DeleteAssetProps = {
  workspaceAsset?: WorkspaceAsset;
  onDeleted: () => Promise<void>;
};

export const DeleteAsset = (props: DeleteAssetProps) => {
  const isCropCircleEnabled = useIsFeatureEnabled(FeatureName.CropCircleRedesign);

  if (isCropCircleEnabled) {
    return <DeleteAssetBase {...props} />;
  }

  return <DeleteAssetOld {...props} />;
};

export const DeleteAssetBase = (props: DeleteAssetProps) => {
  const [showAlert, setShowAlert] = useState(false);
  const { account } = useCurrentAccount();
  const { t } = useTranslation();
  const { mutateAsync } = useDeleteMutation({
    modelName: 'asset',
    query: DELETE_ASSET,
  });

  const onDelete = async () => {
    const workspaceAsset = props.workspaceAsset;

    if (!workspaceAsset) {
      return;
    }

    const input = {
      id: workspaceAsset.asset.id,
      workspaces: [{ id: workspaceAsset.workspace.id }],
    };

    await mutateAsync({ input });
    await props.onDeleted();
  };

  const isOemWorkspace = account?.workspace?.type === WorkspaceType.Oem;

  return (
    account?.hasPermission(PermissionValues.asset.delete) &&
    !isOemWorkspace && (
      <>
        <Pressable onPress={() => setShowAlert(true)}>
          <Button 
            variant='rectangular'
            state='outline'
            onPress={() => setShowAlert(true)}
            style={styles.buttons}
          >
            {t('asset.delete.button')}
          </Button>
        </Pressable>
        <Modal
          visible={showAlert}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setShowAlert(false)}
        >
          <View style={styles.modalBackdrop}>
            <View style={styles.modalContainer}>
              <Text style={styles.modalTitle}>
                {t('asset.detail.delete_asset')}
              </Text>
              <Text style={styles.modalMessage}>
                {t('asset.detail.delete_warning')}
              </Text>
              <Button 
                variant='rectangular'
                state='warning'
                onPress={onDelete}
                style={styles.buttons}
              >
                {t('asset.delete.confirm')}
              </Button>
              <Button 
                variant='rectangular'
                state='default'
                onPress={() => setShowAlert(false)}
                style={styles.buttons}
              >
                {t('asset.delete.cancel')}
              </Button>
            </View>
          </View>
        </Modal>
      </>
    )
  );
};

const DELETE_ASSET = `
  mutation Mutation($input: MutateAssetInput!) {
    deleteAsset(input: $input) {
      id
    }
  }
`;

const styles = StyleSheet.create({
  modalBackdrop: {
    backgroundColor: Colors.blackInactive,
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    ...Platform.select({
      web: {
        overflow: 'hidden',
        backfaceVisibility: 'hidden',
      },
    }),
    backgroundColor: Colors.white,
    padding: 16,
    position: 'absolute',
    right: 0,
    bottom: 0,
    left: 0,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.black,
    textAlign: 'center',
    marginBottom: 12,
  },
  modalMessage: {
    fontSize: 14,
    fontWeight: '400',
    color: Colors.grayText,
    textAlign: 'center',
    marginBottom: 16,
  },
  buttons: {
    marginBottom: 12,
    width: '100%',
    height: 48
  },
});
